import React from 'react';
import { motion } from 'framer-motion';

const baseChipStyle = {
  border: '1px solid rgba(255,255,255,0.15)',
  color: 'rgba(255,255,255,0.75)',
  background: 'transparent',
};

function styleForChip(chip, isSelected, disabled, multiSelect) {
  if (disabled) {
    return {
      ...baseChipStyle,
      opacity: 0.3,
      cursor: 'not-allowed',
    };
  }

  if (isSelected) {
    if (multiSelect) {
      return {
        border: '1px solid #39FF14',
        color: '#39FF14',
        background: 'rgba(57,255,20,0.08)',
      };
    }
    return {
      border: '1px solid #FFE800',
      background: '#FFE800',
      color: '#000',
      fontWeight: 600,
    };
  }

  if (chip.type === 'action') {
    return {
      border: '1px solid rgba(255,232,0,0.45)',
      color: '#FFE800',
      background: 'transparent',
    };
  }

  if (chip.type === 'workflow') {
    return {
      border: `1px solid ${chip.borderColor || 'rgba(255,255,255,0.15)'}`,
      color: chip.textColor || 'rgba(255,255,255,0.85)',
      background: chip.bgColor || 'rgba(255,255,255,0.04)',
    };
  }

  if (chip.type === 'expand') {
    return {
      border: '1px dashed rgba(255,255,255,0.35)',
      color: 'rgba(255,255,255,0.75)',
      background: 'transparent',
    };
  }

  if (chip.type === 'skip') {
    return {
      border: '1px solid rgba(255,255,255,0.12)',
      color: 'rgba(255,255,255,0.55)',
      background: 'transparent',
    };
  }

  return baseChipStyle;
}

export default function ConversationChips({
  chips,
  onSelect,
  multiSelect = false,
  maxSelect = 3,
  selectedChips = [],
  onDone,
  onEdit,
}) {
  const hasSelection = selectedChips.length > 0;
  const hitMax = multiSelect && selectedChips.length >= maxSelect;

  const isSelected = (label) => selectedChips.includes(label);

  return (
    <div data-testid="finder-chip-zone">
      <div className="flex flex-wrap gap-2" data-testid="finder-chips-list">
        {(chips || []).map((chip, index) => {
          const item = typeof chip === 'string' ? { label: chip } : chip;
          const selected = isSelected(item.label);
          const disabled = !selected && hitMax;

          return (
            <motion.button
              key={`${item.label}-${index}`}
              type="button"
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.18, delay: index * 0.04 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => !disabled && onSelect(item)}
              className="px-3.5 py-2 text-[13px] rounded-full inline-flex items-center gap-1.5 transition-colors basis-[calc(50%-4px)] sm:basis-auto justify-center sm:justify-start"
              style={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                ...styleForChip(item, selected, disabled, multiSelect),
              }}
              disabled={disabled}
              data-testid={`finder-chip-${index}`}
            >
              {multiSelect && (
                <span
                  className="inline-flex items-center justify-center w-3.5 h-3.5 border"
                  style={{
                    borderColor: selected ? '#39FF14' : 'rgba(255,255,255,0.35)',
                    color: selected ? '#39FF14' : 'transparent',
                    fontSize: 9,
                    lineHeight: 1,
                  }}
                >
                  ✓
                </span>
              )}
              {item.label}
            </motion.button>
          );
        })}
      </div>

      {multiSelect && hasSelection && (
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={onDone}
            className="px-3.5 py-1.5 text-sm font-semibold rounded-none"
            style={{ background: 'var(--glow-1)', color: 'var(--bg-base)' }}
            data-testid="finder-chip-done"
          >
            Done →
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="px-3.5 py-1.5 text-sm border rounded-none"
            style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-secondary)' }}
            data-testid="finder-chip-edit"
          >
            Edit
          </button>
          {hitMax && (
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              max {maxSelect} selected
            </span>
          )}
        </div>
      )}

      <p className="mt-2 text-[11px] tracking-wide" style={{ color: 'var(--text-muted)' }}>
        Or type your own answer ↑
      </p>
    </div>
  );
}
